import type {
  AddDynamicPortfolioProductRequest,
  AssetClass,
  AssetClassStatistics,
  AssetClassStatisticsRequest,
  ClientPreference,
  CollectionRiskScoresResponse,
  CollectionSettings,
  CorrelationFetchResult,
  CorrelationMatrixData,
  CostType,
  CreateAssetClassRequest,
  CreateClientPreferenceRequest,
  CreateCollectionFromTemplateRequest,
  CreateCollectionRequest,
  CreateDynamicPortfolioRequest,
  CreateGoalTypeRequest,
  CreateMandateTemplateRequest,
  CreatePlatformRequest,
  CreatePortfolioRequest,
  CreateProductImportRequest,
  CreateProductImportResponse,
  CreateProductRequest,
  CreateWrapperTypeInput,
  CurrencyFeesResponse,
  DeleteCheckResponse,
  DynamicPortfolio,
  DynamicPortfolioProductItem,
  DynamicPortfolioProductMutationResponse,
  MandateTemplate,
  Portfolio,
  PortfolioCollection,
  PortfolioCollectionGoalType,
  PortfolioCollectionListItem,
  PortfolioCollectionPlatform,
  PortfolioCost,
  PortfolioWithProducts,
  Product,
  ProductAllocationOverviewItem,
  ProductAnalysisResponse,
  ProductCostClass,
  ProductImportJob,
  ProductListItem,
  ProductSelectionItem,
  ProductType,
  ProductTypeAssessment,
  ProductTypeAssessmentMode,
  ProductXrayResponse,
  ReorderGoalTypesRequest,
  ReorderPlatformsRequest,
  ReorderSelectionRequest,
  SearchInstrumentsRequest,
  SearchInstrumentsResponse,
  SuitabilityOptions,
  SustainabilityConfigResponse,
  UpdateAssetClassRequest,
  UpdateClientPreferenceRequest,
  UpdateCollectionMandateSettingsRequest,
  UpdateCollectionMandateSettingsResponse,
  UpdateCollectionRequest,
  UpdateCollectionSettingsRequest,
  UpdateCurrencyFeesRequest,
  UpdateDynamicPortfolioProductRequest,
  UpdateDynamicPortfolioRequest,
  UpdateGoalTypeRequest,
  UpdateMandateTemplateRequest,
  UpdatePlatformRequest,
  UpdatePortfolioRequest,
  UpdateProductRequest,
  UpdateSustainabilityConfigRequest,
  UpdateWrapperTypeInput,
  UpsertProductTypeAssessmentRequest,
  WrapperType,
} from "@repo/shared/types";
import { del, get, patch, post, put } from "@/lib/api/_client";

const BASE = "/internal/portfolio-collections";

// --- Collections CRUD ---

export function list(): Promise<PortfolioCollectionListItem[]> {
  return get(BASE);
}

export function getById(id: string): Promise<PortfolioCollectionListItem> {
  return get(`${BASE}/${id}`);
}

export function create(data: CreateCollectionRequest): Promise<PortfolioCollection> {
  return post<PortfolioCollection>(BASE, data satisfies CreateCollectionRequest);
}

export function createFromTemplate(
  data: CreateCollectionFromTemplateRequest,
): Promise<PortfolioCollection> {
  return post<PortfolioCollection>(
    `${BASE}/from-template`,
    data satisfies CreateCollectionFromTemplateRequest,
  );
}

export function update(id: string, data: UpdateCollectionRequest): Promise<{ success: boolean }> {
  return patch<{ success: boolean }>(`${BASE}/${id}`, data satisfies UpdateCollectionRequest);
}

export function checkDelete(id: string): Promise<DeleteCheckResponse> {
  return get<DeleteCheckResponse>(`${BASE}/${id}/delete-check`);
}

export function remove(id: string): Promise<{ success: boolean }> {
  return del(`${BASE}/${id}`);
}

export function toggleActive(id: string): Promise<{ success: boolean }> {
  return post(`${BASE}/${id}/toggle-active`, {});
}

// --- Collection Settings ---

export function getSettings(id: string): Promise<CollectionSettings> {
  return get(`${BASE}/${id}/settings`);
}

export function updateSettings(
  id: string,
  data: UpdateCollectionSettingsRequest,
): Promise<{ success: boolean }> {
  return patch<{ success: boolean }>(
    `${BASE}/${id}/settings`,
    data satisfies UpdateCollectionSettingsRequest,
  );
}

/** QL-375 — the mandate switch and its four rules; omitted fields are left unchanged. */
export function updateMandateSettings(
  id: string,
  data: UpdateCollectionMandateSettingsRequest,
): Promise<UpdateCollectionMandateSettingsResponse> {
  return patch(
    `${BASE}/${id}/mandate-settings`,
    data satisfies UpdateCollectionMandateSettingsRequest,
  );
}

// --- Currency Fees ---

export function getCurrencyFees(id: string): Promise<CurrencyFeesResponse> {
  return get(`${BASE}/${id}/currency-fees`);
}

export function updateCurrencyFees(
  id: string,
  data: UpdateCurrencyFeesRequest,
): Promise<CurrencyFeesResponse> {
  return patch(`${BASE}/${id}/currency-fees`, data);
}

// --- Products ---

export function listProductAllocations(
  collectionId: string,
  params?: { search?: string },
): Promise<ProductAllocationOverviewItem[]> {
  const search = params?.search?.trim();
  const qs = search ? `?search=${encodeURIComponent(search)}` : "";
  return get(`${BASE}/${collectionId}/product-allocations-overview${qs}`);
}

export function listProducts(
  collectionId: string,
  params?: { search?: string },
): Promise<ProductListItem[]> {
  const search = params?.search?.trim();
  const qs = search ? `?search=${encodeURIComponent(search)}` : "";
  return get(`${BASE}/${collectionId}/products${qs}`);
}

export function getProduct(collectionId: string, productId: string): Promise<Product> {
  return get(`${BASE}/${collectionId}/products/${productId}`);
}

export function createProduct(collectionId: string, data: CreateProductRequest): Promise<Product> {
  return post<Product>(`${BASE}/${collectionId}/products`, data satisfies CreateProductRequest);
}

export function getProductAnalysis(
  collectionId: string,
  productId: string,
): Promise<ProductAnalysisResponse> {
  return post(`${BASE}/${collectionId}/products/${productId}/analysis`);
}

export function getProductXray(
  collectionId: string,
  productId: string,
  lang: string,
): Promise<ProductXrayResponse> {
  return post(`${BASE}/${collectionId}/products/${productId}/xray?lang=${lang}`);
}

export function updateProduct(
  collectionId: string,
  productId: string,
  data: UpdateProductRequest,
): Promise<Product> {
  return put<Product>(
    `${BASE}/${collectionId}/products/${productId}`,
    data satisfies UpdateProductRequest,
  );
}

export function deleteProduct(
  collectionId: string,
  productId: string,
): Promise<{ success: boolean }> {
  return del(`${BASE}/${collectionId}/products/${productId}`);
}

/** QL-168 — asked before the delete confirmation opens. */
export function checkDeleteProduct(
  collectionId: string,
  productId: string,
): Promise<DeleteCheckResponse> {
  return get<DeleteCheckResponse>(`${BASE}/${collectionId}/products/${productId}/delete-check`);
}

export function toggleProductActive(
  collectionId: string,
  productId: string,
): Promise<{ success: boolean }> {
  return post(`${BASE}/${collectionId}/products/${productId}/toggle-active`, {});
}

export function startProductImport(
  collectionId: string,
  payload: CreateProductImportRequest,
): Promise<CreateProductImportResponse> {
  return post(
    `${BASE}/${collectionId}/products/import`,
    payload satisfies CreateProductImportRequest,
  );
}

export function getProductImportJob(
  collectionId: string,
  jobId: string,
): Promise<ProductImportJob> {
  return get(`${BASE}/${collectionId}/products/import/${jobId}`);
}

export function searchInstruments(
  data: SearchInstrumentsRequest,
): Promise<SearchInstrumentsResponse> {
  return post(`${BASE}/products/search-instruments`, data satisfies SearchInstrumentsRequest);
}

export function getInstrumentMetadata(data: Record<string, unknown>): Promise<Record<string, any>> {
  return post(`${BASE}/products/instrument-metadata`, data);
}

export function getSuitabilityOptions(
  collectionId: string,
  productId: string,
): Promise<SuitabilityOptions> {
  return get(`${BASE}/${collectionId}/products/${productId}/suitability-options`);
}

export function getNewProductSuitabilityOptions(collectionId: string): Promise<SuitabilityOptions> {
  return get(`${BASE}/${collectionId}/products/_new/suitability-options`);
}

// --- Portfolios ---

export function listPortfolios(collectionId: string): Promise<Portfolio[]> {
  return get(`${BASE}/${collectionId}/portfolios`);
}

export function getCollectionRiskScores(collectionId: string): Promise<number[]> {
  return get<CollectionRiskScoresResponse>(`${BASE}/${collectionId}/risk-scores`).then(
    (r) => r.scores,
  );
}

export function getPortfolio(
  collectionId: string,
  portfolioId: string,
): Promise<PortfolioWithProducts> {
  return get(`${BASE}/${collectionId}/portfolios/${portfolioId}`);
}

export function createPortfolio(
  collectionId: string,
  data: CreatePortfolioRequest,
): Promise<Portfolio> {
  return post(`${BASE}/${collectionId}/portfolios`, data satisfies CreatePortfolioRequest);
}

export function updatePortfolio(
  collectionId: string,
  portfolioId: string,
  data: UpdatePortfolioRequest,
): Promise<{ success: boolean }> {
  return patch(
    `${BASE}/${collectionId}/portfolios/${portfolioId}`,
    data satisfies UpdatePortfolioRequest,
  );
}

export function deletePortfolio(
  collectionId: string,
  portfolioId: string,
): Promise<{ success: boolean }> {
  return del(`${BASE}/${collectionId}/portfolios/${portfolioId}`);
}

/** QL-168 — asked before the delete confirmation opens. */
export function checkDeletePortfolio(
  collectionId: string,
  portfolioId: string,
): Promise<DeleteCheckResponse> {
  return get<DeleteCheckResponse>(`${BASE}/${collectionId}/portfolios/${portfolioId}/delete-check`);
}

export function togglePortfolioActive(
  collectionId: string,
  portfolioId: string,
): Promise<{ success: boolean }> {
  return post(`${BASE}/${collectionId}/portfolios/${portfolioId}/toggle-active`, {});
}

// --- Portfolio Products ---

export function addPortfolioProduct(
  collectionId: string,
  portfolioId: string,
  data: { productId: string; weight: number },
): Promise<unknown> {
  return post(`${BASE}/${collectionId}/portfolios/${portfolioId}/products`, data);
}

export function updatePortfolioProduct(
  collectionId: string,
  portfolioId: string,
  ppId: string,
  data: { weight: number },
): Promise<{ success: boolean }> {
  return patch(`${BASE}/${collectionId}/portfolios/${portfolioId}/products/${ppId}`, data);
}

export function deletePortfolioProduct(
  collectionId: string,
  portfolioId: string,
  ppId: string,
): Promise<{ success: boolean }> {
  return del(`${BASE}/${collectionId}/portfolios/${portfolioId}/products/${ppId}`);
}

// --- Cost Types ---

export function listCostTypes(collectionId: string): Promise<CostType[]> {
  return get(`${BASE}/${collectionId}/cost-types`);
}

export function createCostType(
  collectionId: string,
  data: Record<string, unknown>,
): Promise<CostType> {
  return post(`${BASE}/${collectionId}/cost-types`, data);
}

export function updateCostType(
  collectionId: string,
  ctId: string,
  data: Record<string, unknown>,
): Promise<{ success: boolean }> {
  return patch(`${BASE}/${collectionId}/cost-types/${ctId}`, data);
}

export function deleteCostType(collectionId: string, ctId: string): Promise<{ success: boolean }> {
  return del(`${BASE}/${collectionId}/cost-types/${ctId}`);
}

export function reorderCostTypes(
  collectionId: string,
  data: { ids: string[] },
): Promise<{ success: boolean }> {
  return post(`${BASE}/${collectionId}/cost-types/reorder`, data);
}

// --- Portfolio Costs ---

export function listPortfolioCosts(collectionId: string): Promise<PortfolioCost[]> {
  return get(`${BASE}/${collectionId}/portfolio-costs`);
}

export function createPortfolioCost(
  collectionId: string,
  data: Record<string, unknown>,
): Promise<PortfolioCost> {
  return post(`${BASE}/${collectionId}/portfolio-costs`, data);
}

export function updatePortfolioCost(
  collectionId: string,
  pcId: string,
  data: Record<string, unknown>,
): Promise<{ success: boolean }> {
  return patch(`${BASE}/${collectionId}/portfolio-costs/${pcId}`, data);
}

export function deletePortfolioCost(
  collectionId: string,
  pcId: string,
): Promise<{ success: boolean }> {
  return del(`${BASE}/${collectionId}/portfolio-costs/${pcId}`);
}

// --- Dynamic Portfolios (QP-185) ---

export function listDynamicPortfolios(collectionId: string): Promise<DynamicPortfolio[]> {
  return get(`${BASE}/${collectionId}/dynamic-portfolios`);
}

export function getDynamicPortfolio(
  collectionId: string,
  dynamicPortfolioId: string,
): Promise<DynamicPortfolio> {
  return get<DynamicPortfolio>(`${BASE}/${collectionId}/dynamic-portfolios/${dynamicPortfolioId}`);
}

export function createDynamicPortfolio(
  collectionId: string,
  data: CreateDynamicPortfolioRequest,
): Promise<DynamicPortfolio> {
  return post<DynamicPortfolio>(
    `${BASE}/${collectionId}/dynamic-portfolios`,
    data satisfies CreateDynamicPortfolioRequest,
  );
}

export function updateDynamicPortfolio(
  collectionId: string,
  dynamicPortfolioId: string,
  data: UpdateDynamicPortfolioRequest,
): Promise<DynamicPortfolio> {
  return patch<DynamicPortfolio>(
    `${BASE}/${collectionId}/dynamic-portfolios/${dynamicPortfolioId}`,
    data satisfies UpdateDynamicPortfolioRequest,
  );
}

export function addDynamicPortfolioProduct(
  collectionId: string,
  dynamicPortfolioId: string,
  data: AddDynamicPortfolioProductRequest,
): Promise<DynamicPortfolioProductItem> {
  return post<DynamicPortfolioProductItem>(
    `${BASE}/${collectionId}/dynamic-portfolios/${dynamicPortfolioId}/products`,
    data satisfies AddDynamicPortfolioProductRequest,
  );
}

export function updateDynamicPortfolioProduct(
  collectionId: string,
  dynamicPortfolioId: string,
  dynamicPortfolioProductId: string,
  data: UpdateDynamicPortfolioProductRequest,
): Promise<DynamicPortfolioProductMutationResponse> {
  return patch<DynamicPortfolioProductMutationResponse>(
    `${BASE}/${collectionId}/dynamic-portfolios/${dynamicPortfolioId}/products/${dynamicPortfolioProductId}`,
    data satisfies UpdateDynamicPortfolioProductRequest,
  );
}

export function deleteDynamicPortfolioProduct(
  collectionId: string,
  dynamicPortfolioId: string,
  dynamicPortfolioProductId: string,
): Promise<DynamicPortfolioProductMutationResponse> {
  return del<DynamicPortfolioProductMutationResponse>(
    `${BASE}/${collectionId}/dynamic-portfolios/${dynamicPortfolioId}/products/${dynamicPortfolioProductId}`,
  );
}

export function deleteDynamicPortfolio(
  collectionId: string,
  dynamicPortfolioId: string,
): Promise<{ success: boolean }> {
  return del(`${BASE}/${collectionId}/dynamic-portfolios/${dynamicPortfolioId}`);
}

/** QL-168 — asked before the delete confirmation opens. */
export function checkDeleteDynamicPortfolio(
  collectionId: string,
  dynamicPortfolioId: string,
): Promise<DeleteCheckResponse> {
  return get<DeleteCheckResponse>(
    `${BASE}/${collectionId}/dynamic-portfolios/${dynamicPortfolioId}/delete-check`,
  );
}

// --- Asset Classes ---

export function listAssetClasses(collectionId: string): Promise<AssetClass[]> {
  return get(`${BASE}/${collectionId}/asset-classes`);
}

export function createAssetClass(
  collectionId: string,
  data: CreateAssetClassRequest,
): Promise<AssetClass> {
  return post<AssetClass>(
    `${BASE}/${collectionId}/asset-classes`,
    data satisfies CreateAssetClassRequest,
  );
}

export function listSelections(
  collectionId: string,
  assetClassId: string,
): Promise<ProductSelectionItem[]> {
  return get(`${BASE}/${collectionId}/asset-classes/${assetClassId}/selections`);
}

export function reorderSelection(
  collectionId: string,
  assetClassId: string,
  body: ReorderSelectionRequest,
): Promise<ProductSelectionItem[]> {
  return patch<ProductSelectionItem[]>(
    `${BASE}/${collectionId}/asset-classes/${assetClassId}/selections/reorder`,
    body satisfies ReorderSelectionRequest,
  );
}

export function updateAssetClass(
  collectionId: string,
  acId: string,
  data: UpdateAssetClassRequest,
): Promise<AssetClass> {
  return patch<AssetClass>(
    `${BASE}/${collectionId}/asset-classes/${acId}`,
    data satisfies UpdateAssetClassRequest,
  );
}

export function deleteAssetClass(
  collectionId: string,
  acId: string,
): Promise<{ success: boolean }> {
  return del(`${BASE}/${collectionId}/asset-classes/${acId}`);
}

/** QL-168 — asked before the delete confirmation opens. */
export function checkDeleteAssetClass(
  collectionId: string,
  acId: string,
): Promise<DeleteCheckResponse> {
  return get<DeleteCheckResponse>(`${BASE}/${collectionId}/asset-classes/${acId}/delete-check`);
}

export function getAssetClassStatistics(
  collectionId: string,
  data: AssetClassStatisticsRequest,
): Promise<AssetClassStatistics> {
  return post<AssetClassStatistics>(
    `${BASE}/${collectionId}/asset-classes/statistics`,
    data satisfies AssetClassStatisticsRequest,
  );
}

// --- Correlations ---

export function getCorrelations(collectionId: string): Promise<CorrelationMatrixData> {
  return get(`${BASE}/${collectionId}/correlations`);
}

export function saveCorrelations(
  collectionId: string,
  data: { matrix: CorrelationMatrixData },
): Promise<{ success: boolean }> {
  return post(`${BASE}/${collectionId}/correlations`, data);
}

export function fetchApiCorrelations(
  collectionId: string,
  data: Record<string, unknown>,
): Promise<CorrelationFetchResult> {
  return post(`${BASE}/${collectionId}/correlations/fetch-api`, data);
}

// --- Product Correlations (QP-164 — Products mode) ---

export function getProductCorrelations(collectionId: string): Promise<CorrelationMatrixData> {
  return get(`${BASE}/${collectionId}/product-correlations`);
}

export function saveProductCorrelations(
  collectionId: string,
  data: { matrix: CorrelationMatrixData },
): Promise<{ success: boolean }> {
  return post(`${BASE}/${collectionId}/product-correlations`, data);
}

// --- Product Types ---

export function listProductTypes(collectionId: string): Promise<ProductType[]> {
  return get(`${BASE}/${collectionId}/product-types`);
}

export function createProductType(
  collectionId: string,
  data: Record<string, unknown>,
): Promise<ProductType> {
  return post(`${BASE}/${collectionId}/product-types`, data);
}

export function updateProductType(
  collectionId: string,
  ptId: string,
  data: Record<string, unknown>,
): Promise<{ success: boolean }> {
  return patch(`${BASE}/${collectionId}/product-types/${ptId}`, data);
}

export function deleteProductType(
  collectionId: string,
  ptId: string,
): Promise<{ success: boolean }> {
  return del(`${BASE}/${collectionId}/product-types/${ptId}`);
}

/** QL-168 — asked before the delete confirmation opens. */
export function checkDeleteProductType(
  collectionId: string,
  ptId: string,
): Promise<DeleteCheckResponse> {
  return get<DeleteCheckResponse>(`${BASE}/${collectionId}/product-types/${ptId}/delete-check`);
}

export function toggleProductTypeActive(
  collectionId: string,
  ptId: string,
): Promise<{ success: boolean }> {
  return post(`${BASE}/${collectionId}/product-types/${ptId}/toggle-active`, {});
}

export function toggleProductTypeDefault(
  collectionId: string,
  ptId: string,
): Promise<{ success: boolean }> {
  return post(`${BASE}/${collectionId}/product-types/${ptId}/toggle-default`, {});
}

// --- Goal Types (QP-187) ---

export function listGoalTypes(collectionId: string): Promise<PortfolioCollectionGoalType[]> {
  return get(`${BASE}/${collectionId}/goal-types`);
}

export function createGoalType(
  collectionId: string,
  data: CreateGoalTypeRequest,
): Promise<PortfolioCollectionGoalType> {
  return post<PortfolioCollectionGoalType>(
    `${BASE}/${collectionId}/goal-types`,
    data satisfies CreateGoalTypeRequest,
  );
}

export function updateGoalType(
  collectionId: string,
  gtId: string,
  data: UpdateGoalTypeRequest,
): Promise<PortfolioCollectionGoalType> {
  return patch<PortfolioCollectionGoalType>(
    `${BASE}/${collectionId}/goal-types/${gtId}`,
    data satisfies UpdateGoalTypeRequest,
  );
}

export function deleteGoalType(collectionId: string, gtId: string): Promise<{ success: boolean }> {
  return del(`${BASE}/${collectionId}/goal-types/${gtId}`);
}

export function reorderGoalTypes(
  collectionId: string,
  orderedIds: string[],
): Promise<{ success: boolean }> {
  return post<{ success: boolean }>(`${BASE}/${collectionId}/goal-types/reorder`, {
    orderedIds,
  } satisfies ReorderGoalTypesRequest);
}

// --- Wrapper Types ---

export function listWrapperTypes(collectionId: string): Promise<WrapperType[]> {
  return get(`${BASE}/${collectionId}/wrapper-types`);
}

export function createWrapperType(
  collectionId: string,
  data: CreateWrapperTypeInput,
): Promise<WrapperType> {
  return post<WrapperType>(
    `${BASE}/${collectionId}/wrapper-types`,
    data satisfies CreateWrapperTypeInput,
  );
}

export function updateWrapperType(
  collectionId: string,
  wtId: string,
  data: UpdateWrapperTypeInput,
): Promise<WrapperType> {
  return patch<WrapperType>(
    `${BASE}/${collectionId}/wrapper-types/${wtId}`,
    data satisfies UpdateWrapperTypeInput,
  );
}

export function deleteWrapperType(
  collectionId: string,
  wtId: string,
): Promise<{ success: boolean }> {
  return del(`${BASE}/${collectionId}/wrapper-types/${wtId}`);
}

/** QL-168 — asked before the delete confirmation opens. */
export function checkDeleteWrapperType(
  collectionId: string,
  wtId: string,
): Promise<DeleteCheckResponse> {
  return get<DeleteCheckResponse>(`${BASE}/${collectionId}/wrapper-types/${wtId}/delete-check`);
}

export function toggleWrapperTypeActive(
  collectionId: string,
  wtId: string,
): Promise<{ success: boolean }> {
  return post(`${BASE}/${collectionId}/wrapper-types/${wtId}/toggle-active`, {});
}

// --- Client Preferences (QP-224) ---

export function listClientPreferences(collectionId: string): Promise<ClientPreference[]> {
  return get(`${BASE}/${collectionId}/client-preferences`);
}

export function createClientPreference(
  collectionId: string,
  data: CreateClientPreferenceRequest,
): Promise<ClientPreference> {
  return post<ClientPreference>(
    `${BASE}/${collectionId}/client-preferences`,
    data satisfies CreateClientPreferenceRequest,
  );
}

export function updateClientPreference(
  collectionId: string,
  prefId: string,
  data: UpdateClientPreferenceRequest,
): Promise<ClientPreference> {
  return patch<ClientPreference>(
    `${BASE}/${collectionId}/client-preferences/${prefId}`,
    data satisfies UpdateClientPreferenceRequest,
  );
}

export function deleteClientPreference(
  collectionId: string,
  prefId: string,
): Promise<{ success: boolean }> {
  return del(`${BASE}/${collectionId}/client-preferences/${prefId}`);
}

// --- Platforms (QP-195) ---

export function listPlatforms(collectionId: string): Promise<PortfolioCollectionPlatform[]> {
  return get(`${BASE}/${collectionId}/platforms`);
}

// --- Mandate templates (QL-375) ---

export function listMandateTemplates(collectionId: string): Promise<MandateTemplate[]> {
  return get(`${BASE}/${collectionId}/mandate-templates`);
}

export function createMandateTemplate(
  collectionId: string,
  data: CreateMandateTemplateRequest,
): Promise<MandateTemplate> {
  return post<MandateTemplate>(
    `${BASE}/${collectionId}/mandate-templates`,
    data satisfies CreateMandateTemplateRequest,
  );
}

export function updateMandateTemplate(
  collectionId: string,
  templateId: string,
  data: UpdateMandateTemplateRequest,
): Promise<MandateTemplate> {
  return patch<MandateTemplate>(
    `${BASE}/${collectionId}/mandate-templates/${templateId}`,
    data satisfies UpdateMandateTemplateRequest,
  );
}

export function toggleMandateTemplateActive(
  collectionId: string,
  templateId: string,
): Promise<MandateTemplate> {
  return post<MandateTemplate>(
    `${BASE}/${collectionId}/mandate-templates/${templateId}/toggle-active`,
    {},
  );
}

export function deleteMandateTemplate(
  collectionId: string,
  templateId: string,
): Promise<{ success: boolean }> {
  return del(`${BASE}/${collectionId}/mandate-templates/${templateId}`);
}

// --- Sustainability config (QP-179) ---

export function getSustainabilityConfig(
  collectionId: string,
): Promise<SustainabilityConfigResponse> {
  return get(`${BASE}/${collectionId}/sustainability-config`);
}

export function updateSustainabilityConfig(
  collectionId: string,
  config: UpdateSustainabilityConfigRequest["config"],
): Promise<SustainabilityConfigResponse> {
  return put<SustainabilityConfigResponse>(`${BASE}/${collectionId}/sustainability-config`, {
    config,
  } satisfies UpdateSustainabilityConfigRequest);
}

export function createPlatform(
  collectionId: string,
  data: CreatePlatformRequest,
): Promise<PortfolioCollectionPlatform> {
  return post<PortfolioCollectionPlatform>(
    `${BASE}/${collectionId}/platforms`,
    data satisfies CreatePlatformRequest,
  );
}

export function updatePlatform(
  collectionId: string,
  platformId: string,
  data: UpdatePlatformRequest,
): Promise<PortfolioCollectionPlatform> {
  return patch<PortfolioCollectionPlatform>(
    `${BASE}/${collectionId}/platforms/${platformId}`,
    data satisfies UpdatePlatformRequest,
  );
}

export function deletePlatform(
  collectionId: string,
  platformId: string,
): Promise<{ success: boolean }> {
  return del(`${BASE}/${collectionId}/platforms/${platformId}`);
}

export function reorderPlatforms(
  collectionId: string,
  orderedIds: string[],
): Promise<{ success: boolean }> {
  return post<{ success: boolean }>(`${BASE}/${collectionId}/platforms/reorder`, {
    orderedIds,
  } satisfies ReorderPlatformsRequest);
}

// --- Product Type Assessment (one per enabled mode, at most two per type) ---

export function getProductTypeAssessments(
  collectionId: string,
  ptId: string,
): Promise<ProductTypeAssessment[]> {
  return get(`${BASE}/${collectionId}/product-types/${ptId}/assessment`);
}

export function upsertProductTypeAssessment(
  collectionId: string,
  ptId: string,
  data: UpsertProductTypeAssessmentRequest,
): Promise<ProductTypeAssessment> {
  return put(
    `${BASE}/${collectionId}/product-types/${ptId}/assessment`,
    data satisfies UpsertProductTypeAssessmentRequest,
  );
}

export function deleteProductTypeAssessment(
  collectionId: string,
  ptId: string,
  mode: ProductTypeAssessmentMode,
): Promise<{ success: boolean }> {
  return del(`${BASE}/${collectionId}/product-types/${ptId}/assessment?mode=${mode}`);
}

// --- Product Cost Classes ---

export function listProductCostClasses(collectionId: string): Promise<ProductCostClass[]> {
  return get(`${BASE}/${collectionId}/product-cost-classes`);
}

export function createProductCostClass(
  collectionId: string,
  data: Record<string, unknown>,
): Promise<ProductCostClass> {
  return post(`${BASE}/${collectionId}/product-cost-classes`, data);
}

export function updateProductCostClass(
  collectionId: string,
  pccId: string,
  data: Record<string, unknown>,
): Promise<{ success: boolean }> {
  return patch(`${BASE}/${collectionId}/product-cost-classes/${pccId}`, data);
}

export function deleteProductCostClass(
  collectionId: string,
  pccId: string,
): Promise<{ success: boolean }> {
  return del(`${BASE}/${collectionId}/product-cost-classes/${pccId}`);
}

/** QL-168 — asked before the delete confirmation opens. */
export function checkDeleteProductCostClass(
  collectionId: string,
  pccId: string,
): Promise<DeleteCheckResponse> {
  return get<DeleteCheckResponse>(
    `${BASE}/${collectionId}/product-cost-classes/${pccId}/delete-check`,
  );
}

export function toggleProductCostClassActive(
  collectionId: string,
  pccId: string,
): Promise<{ success: boolean }> {
  return post(`${BASE}/${collectionId}/product-cost-classes/${pccId}/toggle-active`, {});
}
